import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    Tooltip,
    styled,
    useMediaQuery,
    Popover,
    Typography,
    Box,
    ListItemText,
} from '@mui/material';
import Sitemap from '../icons/sitemap.svg';
import UserCircleIcon from '../icons/user-circle.svg';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useTheme } from '@mui/material/styles';
import { useState, MouseEvent, useMemo, cloneElement, useCallback } from 'react';
import { useLayoutState } from '../context/LayoutContext/LayoutContext';
import { useMiniSidebar } from '../context/MiniSidebarContext';
import { getLinkAccessInfo } from '../RouteAccess';
import UserHelper from '../helpers/UserHelper';
import Utils from '../utilities/utils';
import { addFullPathsToNavigation } from '../utilities/navigationUtils';
import { getBaseNavigation } from '../structure';
import SiteSelectionDialog from '../components/SiteSelectionDialog/SiteSelectionDialog';
import MobileSidebar from './MobileSidebar';

import {
    useAppGlobalDispatch,
    useLogOut,
    useVrsTranslationState,
    useConfigState,
    useAppGlobalState,
    useAppSiteState,
    useAppUserState
} from '../context/AppContext/AppContext';
import { canAccessExternalVrsPage } from '../RouteAccess';
import AboutDialog from '../components/AboutDialog/AboutDialog';
import EditProfileDialog from '../vrspages/profileandworkschedule/EditProfileDialog/EditProfileDialog';
import logo from '../icons/vj-logo-white.svg';
import {
    myVJBlack900,
    myVJBlack800,
    myVJBlack750,
    myVJBlack700,
    myVJBlue200,
    myVJWhite,
    myVJPurple50,
    myVJPurple200,
    myVJBlack400
} from '../constants/brandingColors';

const MINI_WIDTH = 63;

const commonPopoverStyling = {
    backgroundColor: myVJBlack900,
    color: myVJWhite,
    minWidth: 245,
    maxWidth: 280,
    borderRadius: '4px',
    marginLeft: 'var(--popover-margin, 4px)', // Default to 4px
    boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.5)',
    padding: 0,
    '& .MuiListItemButton-root': {
        height: '42px',
        padding: '0 16px',
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: '16px',
        '&.Mui-selected': {
            backgroundColor: myVJBlue200,
            color: myVJWhite,
        },
    },
    '& .MuiTypography-root': {
        color: 'inherit',
        fontSize: '16px',
    },
    '& .MuiListItem-root': {
        padding: 0,
    },
    '& .MuiDivider-root': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        margin: 0,
    },
    // Style for the header
    '& .popover-header': {
        backgroundColor: myVJBlack900, // Already using the imported color
        color: myVJWhite,
        padding: '10px 16px',
        fontWeight: 400,
        fontSize: '16px',
        width: '100%',
        marginBottom: 0,
    },
    // Style for selected/expanded sections
    '& .expanded-section': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
    }
};

const StyledListItemIcon = styled(ListItemIcon)(() => ({
    minWidth: 'auto',
    color: 'inherit',
    justifyContent: 'center',
    transition: 'color 0.2s ease-in-out', // Smooth color transition
    '&:hover': {
        backgroundColor: myVJBlack800,
        color: myVJWhite,
        '& .MuiListItemIcon-root': {
            color: myVJWhite,
        }
    },
    '.Mui-selected &': {
        '&:hover': {
            backgroundColor: 'transparent !important',
            color: 'inherit !important',
        }
    },
}));

const LogoSection = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    height: '48px',
    width: '48px',
    margin: '15px 5px', // Adjusted margin for better spacing
    cursor: 'pointer', // Add cursor pointer
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)'
    },
});

const LogoImage = styled('img')({
    width: '31.478px',
    height: '24x',
    objectFit: 'fill',
    flexSrink: 0
});

const NavigationContainer = styled('div')({
    flex: 1,
    overflowY: 'auto',
});

const CircularIconButton = styled(Box)<{ disabled?: boolean }>(({ disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    margin: '2px auto', // Reduced margin for tighter spacing
    transition: 'background-color 0.2s',
    position: 'relative', // Ensure it's above the background
    zIndex: 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
    '&:hover': {
        backgroundColor: disabled ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
    },
}));

const FooterContainer = styled('div')({
    marginTop: 'auto',
    padding: '8px 8px', // Increased padding for the ellipse background
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px', // Reduced gap between buttons
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '6px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '48px', // Width of the ellipse
        height: '144px', // Height to cover all three buttons with some padding
        backgroundColor: myVJBlack800,
        borderRadius: '24px', // Make it elliptical/rounded
        zIndex: 0, // Behind the buttons
    }
});


const StyledListItemButton = styled(ListItemButton)(() => ({
    justifyContent: 'center',
    color: 'rgba(255, 255, 255, 0.7)',
    cursor: 'pointer',
    borderRadius: '8px',
    margin: '4px auto',
    width: '40px',
    height: '40px',
    minHeight: '40px',
    padding: '0',
    // Completely disable all hover effects
    '&:hover': {
        backgroundColor: 'transparent !important',
        color: 'rgba(255, 255, 255, 0.7) !important',
    },
    '&:focus': {
        backgroundColor: 'transparent !important',
    },
    '&:active': {
        backgroundColor: 'transparent !important',
    },
    '&.MuiButtonBase-root': {
        '&:hover': {
            backgroundColor: 'transparent !important',
        }
    },
    '&.Mui-selected': {
        backgroundColor: myVJBlack800,
        color: myVJWhite,
        '& .MuiListItemIcon-root': {
            color: myVJWhite,
        },
        '&:hover': {
            backgroundColor: myVJBlack800 + ' !important', // Keep the same background
            color: myVJWhite + ' !important', // Keep the same color
            // Ensure child icons don't change
            '& .MuiListItemIcon-root': {
                backgroundColor: 'transparent !important',
                color: myVJWhite + ' !important',
            }
        },
    },
    '&.Mui-disabled': {
        cursor: 'not-allowed',
        opacity: 0.5,
        '&:hover': {
            backgroundColor: 'transparent !important',
        }
    },
}));

interface MiniSidebarItemProps {
    item: any;
    onNavigate: (path: string) => void;
    onToggleExpand?: (segment: string) => void;
}

const DesktopSiteSelection = ({ showSiteSelectionDialog, setShowSiteSelectionDialog, isPureDesignUser }) => {
    const { _T } = useVrsTranslationState();

    const handleClick = () => {
        setShowSiteSelectionDialog(true);
    };

    const onCloseSiteSelectionDialog = useCallback(() => {
        setShowSiteSelectionDialog(false);
    }, []);

    return (
        <>
            <Tooltip title={_T("Site Selection")} placement="right" arrow>
                <CircularIconButton
                    disabled={isPureDesignUser}
                    onClick={isPureDesignUser ? undefined : handleClick}
                    aria-label="Open site selection dialog"
                    role="button"
                    sx={{
                        backgroundColor: myVJBlack800,
                        '&:hover': {
                            backgroundColor: myVJBlack750,
                        }
                    }}
                >
                    <img src={Sitemap} alt="Configuration" style={{ width: 24, height: 24 }} />
                </CircularIconButton>
            </Tooltip>

            {showSiteSelectionDialog && (
                <SiteSelectionDialog
                    open={showSiteSelectionDialog}
                    onClose={onCloseSiteSelectionDialog}
                />
            )}
        </>
    );
};

const MiniSidebarItem = ({ item, onNavigate }: MiniSidebarItemProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [expandedSecondLevel, setExpandedSecondLevel] = useState<string | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        if (item.children && item.children.length > 0) {
            setAnchorEl(event.currentTarget);
            setExpandedSecondLevel(null);
        } else if (item.fullPath) {
            onNavigate(item.fullPath);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
        setExpandedSecondLevel(null);
    };

    const handleChildClick = (child: any) => {
        if (child.children && child.children.length > 0) {
            setExpandedSecondLevel(expandedSecondLevel === child.segment ? null : child.segment);
        } else if (child.fullPath) {
            onNavigate(child.fullPath);
            handleClose();
        }
    };

    const handleGrandChildClick = (grandChild: any) => {
        onNavigate(grandChild.fullPath);
        handleClose();
    };

    if (item.hidden || item.kind === 'header' || item.kind === 'divider') {
        return null;
    }

    return (
        <>
            <Tooltip title={item.title} placement="right" arrow>
                <ListItem disablePadding>
                    <StyledListItemButton
                        disableRipple
                        onClick={handleClick}
                        disabled={item.disabled}
                        aria-label={
                            item.children && item.children.length > 0
                                ? `Open ${item.title} menu`
                                : `Navigate to ${item.title}`
                        }
                        role={item.children && item.children.length > 0 ? 'button' : 'menuitem'}
                    >
                        <StyledListItemIcon sx={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            padding: '10px',
                            color: 'inherit',
                            justifyContent: 'center'
                        }}>
                            {item.icon}
                        </StyledListItemIcon>
                    </StyledListItemButton>
                </ListItem>
            </Tooltip>

            {item.children && item.children.length > 0 && (
                <DesktopPopoverMenu
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    title={item.title}
                    expandedSecondLevel={expandedSecondLevel}
                    onChildClick={handleChildClick}
                    onGrandChildClick={handleGrandChildClick}
                    isFooterPopover={false}
                >
                    {item.children}
                </DesktopPopoverMenu>
            )}
        </>
    );
};

const DesktopUserProfile = () => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [showAboutDialog, setShowAboutDialog] = useState(false);
    const [showEditProfileDialog, setShowEditProfileDialog] = useState(false);

    const { userProfileLarge, isAdmin } = useAppUserState();
    const { vrsAbilities } = useAppSiteState();
    const { _T } = useVrsTranslationState();
    const configState = useConfigState();
    const userDispatch = useAppGlobalDispatch();
    const logOut = useLogOut();


    const open = Boolean(anchorEl);
    const handleClick = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const onCloseAboutDialog = useCallback(() => {
        setShowAboutDialog(false);
        setAnchorEl(null);
    }, []);

    const onCloseEditProfileDialog = useCallback(() => {
        setShowEditProfileDialog(false);
        setAnchorEl(null);
    }, []);

    const isUserConfigImporter = !!userProfileLarge?.ListMember?.find(
        (el) => (el.Id === 'config_import' || el.Id === 'early_accessor') && el.MemberType === 'admin'
    );

    const canUserAccessNewDesign = !!userProfileLarge?.ListMember?.find(
        (el) => (el.Id === 'new_design_user') && el.MemberType === 'admin'
    );

    const hasAccessVrs = useCallback(
        (requestedClaim) =>
            canAccessExternalVrsPage({
                requestedClaim,
                vrsAbilities,
                isAdmin,
                useForAuthorizationCheck: false,
                isUserConfigImporter,
                canUserAccessNewDesign,
                isCurrentSiteExternal: configState.appDataInitialization.firstSiteData.isExternal,
            }),
        [isAdmin, vrsAbilities, isUserConfigImporter, configState.appDataInitialization.firstSiteData.isExternal]
    );

    const userMenuItems = [
        ...(hasAccessVrs('edit-profile') ? [{
            title: _T('Profile'),
            action: () => setShowEditProfileDialog(true)
        }] : []),
        {
            title: _T('About'),
            action: () => setShowAboutDialog(true)
        },
        {
            title: _T('Logout'),
            action: () => logOut(userDispatch)
        }
    ];

    return (
        <>
            <Tooltip title="User Profile" placement="right" arrow>
                <CircularIconButton
                    onClick={handleClick}
                    aria-label="Open user profile menu"
                    role="button"
                    sx={{
                        backgroundColor: myVJPurple50,
                        '&:hover': { backgroundColor: myVJPurple200 }
                    }}
                >
                    {userProfileLarge?.FirstName && userProfileLarge?.LastName ? (
                        <Typography sx={{
                            color: myVJBlack900,
                            fontSize: '14px',
                            fontWeight: '700'
                        }}>
                            {`${userProfileLarge.FirstName.charAt(0)}${userProfileLarge.LastName.charAt(0)}`}
                        </Typography>
                    ) : (
                        <img src={UserCircleIcon} alt="User Icon" style={{ width: 24, height: 24 }} />
                    )}
                </CircularIconButton>
            </Tooltip>

            <DesktopPopoverMenu
                open={open}
                isFooterPopover={true}
                anchorEl={anchorEl}
                onClose={handleClose}
                title={_T('My account')}
                expandedSecondLevel={null}
                onChildClick={(item) => {
                    item.action();
                    handleClose();
                }}
                onGrandChildClick={() => { }}
            >
                {userMenuItems.map((item, index) => ({
                    ...item,
                    segment: `user-action-${index}` // Add segment for key prop
                }))}
            </DesktopPopoverMenu>

            {/* Dialogs remain the same */}
            {showAboutDialog && (
                <AboutDialog open={showAboutDialog} onClose={onCloseAboutDialog} />
            )}
            {showEditProfileDialog && (
                <EditProfileDialog open={showEditProfileDialog} onClose={onCloseEditProfileDialog} />
            )}
        </>
    );
};


const Dialogs = ({
    showAboutDialog,
    showEditProfileDialog,
    showSiteSelectionDialog,
    onCloseAboutDialog,
    onCloseEditProfileDialog,
    onCloseSiteSelectionDialog
}) => <>
        {showAboutDialog && (
            <AboutDialog
                open={showAboutDialog}
                onClose={onCloseAboutDialog}
            />
        )}
        {showEditProfileDialog && (
            <EditProfileDialog
                open={showEditProfileDialog}
                onClose={onCloseEditProfileDialog}
            />
        )}
        {showSiteSelectionDialog && (
            <SiteSelectionDialog
                open={showSiteSelectionDialog}
                onClose={onCloseSiteSelectionDialog}
            />
        )}
    </>;

const DesktopSettingsProfile = ({ configurationItem, handleNavigate, isPureDesignUser }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const [expandedSecondLevel, setExpandedSecondLevel] = useState<string | null>(null);


    const handleChildClick = (child: any) => {
        if (child.children && child.children.length > 0) {
            setExpandedSecondLevel(expandedSecondLevel === child.segment ? null : child.segment);
        } else if (child.fullPath) {
            handleNavigate(child.fullPath);
            handleClose();
        }
    };

    const handleGrandChildClick = (grandChild: any) => {
        handleNavigate(grandChild.fullPath);
        handleClose();
    };

    if (!configurationItem || !configurationItem.children) {
        return null;
    }

    return (
        <>
            <Tooltip title={configurationItem.title} placement="right" arrow>
                <CircularIconButton
                    disabled={isPureDesignUser}
                    onClick={isPureDesignUser ? undefined : handleClick}
                    aria-label={`Open ${configurationItem.title} menu`}
                    role="button"
                    sx={{
                        backgroundColor: myVJBlack800,
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                            backgroundColor: myVJBlack750,
                        }
                    }}
                >
                    {cloneElement(configurationItem.icon, {
                        style: {
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '20px',
                            width: '20px',
                            height: '20px'
                        }
                    })}
                </CircularIconButton>
            </Tooltip>

            <DesktopPopoverMenu
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                title={configurationItem.title}
                expandedSecondLevel={expandedSecondLevel}
                onChildClick={handleChildClick}
                onGrandChildClick={handleGrandChildClick}
                isFooterPopover={true}
            >
                {configurationItem.children}
            </DesktopPopoverMenu>
        </>
    );
};

interface DesktopPopoverMenuProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    title: string;
    children: any[];
    expandedSecondLevel: string | null;
    onChildClick: (child: any) => void;
    onGrandChildClick: (grandChild: any) => void;
    isFooterPopover: boolean;
}

const DesktopPopoverMenu = ({
    open,
    anchorEl,
    onClose,
    title,
    children,
    expandedSecondLevel,
    onChildClick,
    onGrandChildClick,
    isFooterPopover
}: DesktopPopoverMenuProps) => {

    const renderChildren = (children: any[]) => {
        return children.map((child) => (
            <Box key={child.segment || child.title}>
                <ListItem disablePadding>
                    <ListItemButton
                        onClick={() => onChildClick(child)}
                        disabled={child.disabled}
                        aria-label={
                            child.children && child.children.length > 0
                                ? `${child.title} submenu, ${expandedSecondLevel === child.segment ? 'expanded' : 'collapsed'}. Click to ${expandedSecondLevel === child.segment ? 'collapse' : 'expand'}.`
                                : `Navigate to ${child.title}`
                        }
                        aria-expanded={child.children && child.children.length > 0 ? expandedSecondLevel === child.segment : undefined}
                        role={child.children && child.children.length > 0 ? 'button' : 'menuitem'}
                        sx={{
                            lineHeight: '24px !important',
                            padding: '16px 20px 16px 24px !important',
                            backgroundColor: myVJBlack800,
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '16px',
                            '&:hover': {
                                backgroundColor: myVJBlue200,
                                color: myVJWhite,
                            },
                            '&.Mui-selected': {
                                backgroundColor: myVJBlue200,
                                color: myVJWhite,
                            },
                            '&.Mui-disabled': {
                                opacity: 0.5,
                                cursor: 'not-allowed',
                            }
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                flex: 1,
                                fontSize: '16px',
                                color: 'inherit',
                                fontWeight: 400,
                            }}
                        >
                            {child.title}
                        </Typography>
                        {child.children && child.children.length > 0 && (
                            <ChevronRightIcon
                                sx={{
                                    transform: expandedSecondLevel === child.segment ? 'rotate(-90deg)' : 'rotate(90deg)',
                                    transition: 'transform 0.2s',
                                    fontSize: '20px',
                                    opacity: 0.8,
                                    color: 'rgba(255, 255, 255, 0.8)',
                                }}
                                aria-hidden="true"
                            />
                        )}
                    </ListItemButton>
                </ListItem>

                {/* Level 3 items */}
                {child.children && child.children.length > 0 && expandedSecondLevel === child.segment && (
                    <div>
                        {child.children.map((grandChild: any) => (
                            <ListItem key={grandChild.segment || grandChild.title} disablePadding>
                                <ListItemButton
                                    id={'husam1'}
                                    onClick={() => onGrandChildClick(grandChild)}
                                    disabled={grandChild.disabled}
                                    aria-label={`Navigate to ${grandChild.title} sub-item`}
                                    role="menuitem"
                                    sx={{
                                        lineHeight: '24px !important',
                                        padding: '16px 20px 16px 24px !important',
                                        backgroundColor: myVJBlack700,
                                        color: 'rgba(255, 255, 255, 0.85)',
                                        fontSize: '16px',
                                        '&:hover': {
                                            backgroundColor: myVJBlue200,
                                            color: myVJWhite,
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: myVJBlue200,
                                            color: myVJWhite,
                                        },
                                        '&.Mui-disabled': {
                                            opacity: 0.5,
                                            cursor: 'not-allowed',
                                        }
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '16px',
                                            color: 'inherit',
                                        }}
                                    >
                                        {grandChild.title}
                                    </Typography>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </div>
                )}
            </Box>
        ));
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            PaperProps={{
                sx: {
                    ...commonPopoverStyling,
                    '--popover-margin': isFooterPopover ? '18px' : '4px', // Dynamic margin
                    borderRadius: '10px',
                }

            }}
            role="menu" 
            aria-label={`${title} navigation menu`}
        >
            <List dense sx={{ padding: 0 }}>
                <Box
                    className="popover-header"
                    role="heading"
                    aria-level={2}
                    sx={{ borderBottom: `1px solid ${myVJBlack400}`, padding: '16px 20px 16px 24px !important', display: 'flex', alignItems: 'center' }}
                >
                    {title}
                </Box>
                {renderChildren(children)}
            </List>
        </Popover>
    );
};


const MenuItem = ({
    item,
    level = 1,
    onNavigate,
    onClose,
    expandedItems = {},
    onToggleExpand,
    isMobile = false,
}) => {
    // Add local state for third-level expansion
    const [localThirdLevelExpanded, setLocalThirdLevelExpanded] = useState({});

    const hasChildren = item.children && item.children.length > 0;

    // For mobile: use global state for level 1, local state for level 2+
    // For desktop: use global state for all levels
    const isExpanded = isMobile && level >= 2
        ? localThirdLevelExpanded[item.segment] || false
        : expandedItems[item.segment] || false;

    // Choose styling based on level and mobile/desktop
    const getButtonStyles = () => {
        return {
            height: level === 3 ? '40px' : '42px',
            padding: level === 3 ? '0 16px 0 32px' : '0 16px',
            backgroundColor: level === 2 ? myVJBlack800 : myVJBlack700,
            fontSize: '16px',
        };
    };

    const handleClick = () => {
        if (hasChildren) {
            if (isMobile && level >= 2) {
                // Handle second and third-level expansion locally for mobile
                setLocalThirdLevelExpanded(prev => ({
                    ...prev,
                    [item.segment]: !prev[item.segment]
                }));
            } else {
                // Handle first level expansion globally, or all levels for desktop
                onToggleExpand(item.segment);
            }
        } else {
            onNavigate(item.fullPath);
            if (onClose) {
                onClose();
            }
        }
    };

    const handleChildToggleExpand = (childSegment) => {
        if (isMobile) {
            // Always handle child expansion locally for mobile
            setLocalThirdLevelExpanded(prev => ({
                ...prev,
                [childSegment]: !prev[childSegment]
            }));
        } else {
            // Pass to parent for desktop
            onToggleExpand(childSegment);
        }
    };

    return (
        <>
            <ListItem disablePadding sx={isMobile ? {
                margin: 0,
                padding: '0 !important',
                '&.MuiListItem-root': {
                    paddingTop: '0 !important',
                    paddingBottom: '0 !important'
                }
            } : {}}>
                <ListItemButton
                    onClick={handleClick}
                    disabled={item.disabled}
                    sx={{
                        ...getButtonStyles(),
                        color: 'rgba(255, 255, 255, 0.9)',
                        '&.Mui-selected': {
                            backgroundColor: myVJBlue200,
                            color: myVJWhite,
                        },
                        '&.Mui-disabled': {
                            opacity: 0.5,
                            cursor: 'not-allowed',
                        }
                    }}
                >
                    {isMobile && level === 1 && (
                        <ListItemIcon sx={{
                            color: 'inherit',
                            minWidth: 40,
                            margin: 0,
                            padding: 0
                        }}>
                            {item.icon}
                        </ListItemIcon>
                    )}

                    <ListItemText
                        primary={item.title}
                        sx={{
                            '& .MuiTypography-root': {
                                fontSize: '16px',
                                fontWeight: 400,
                                color: 'inherit'
                            }
                        }}
                    />

                    {hasChildren && (
                        <ChevronRightIcon
                            sx={{
                                transform: isExpanded ? 'rotate(-90deg)' : 'rotate(90deg)',
                                transition: 'transform 0.2s',
                                fontSize: isMobile ? (level === 1 ? '24px' : '20px') : '18px',
                                opacity: 0.8,
                                color: 'rgba(255, 255, 255, 0.8)',
                            }}
                            aria-hidden="true"
                        />
                    )}
                </ListItemButton>
            </ListItem>

            {/* Render children recursively */}
            {hasChildren && isExpanded && (
                <div>
                    {item.children.map((child) => (
                        <MenuItem
                            key={child.segment || child.title}
                            item={child}
                            level={level + 1}
                            onNavigate={onNavigate}
                            onClose={onClose}
                            expandedItems={isMobile ? localThirdLevelExpanded : expandedItems}
                            onToggleExpand={handleChildToggleExpand}
                            isMobile={isMobile}
                        />
                    ))}
                </div>
            )}
        </>
    );
};

export default function Sidebar() {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const { isSidebarOpened } = useLayoutState();
    const [showSiteSelectionDialog, setShowSiteSelectionDialog] = useState(false);

    // Call useMiniSidebar at the top level, not conditionally
    const { mobileOpen, customNavigate, closeMobile } = useMiniSidebar();

    const handleSiteSelectionNavigation = () => {
        setShowSiteSelectionDialog(true);
    };

    const onCloseSiteSelectionDialog = useCallback(() => {
        setShowSiteSelectionDialog(false);
    }, []);

    // App state hooks
    const { initialised } = useAppGlobalState();
    const { selectedSiteId, vrsAbilities } = useAppSiteState();
    const { _T } = useVrsTranslationState();
    const configState = useConfigState();
    const { userProfile, userProfileLarge, isAuthenticated } = useAppUserState();

    const smallOrHigher = useMediaQuery(theme.breakpoints.up('sm'));
    const smallOnly = useMediaQuery(theme.breakpoints.only('sm'));

    // User permissions
    const { isAdmin, isPureDesignUser } = useAppGlobalState();
    const { sites } = useAppSiteState();

    const getSelectedSite = () => {
        if (sites && sites.length > 0 && selectedSiteId) {
            return sites.find((el) => el.id === selectedSiteId);
        }

        if (sites && sites.length > 0 && !selectedSiteId) {
            return sites[0];
        }

        return null;
    };

    const site = getSelectedSite();
    const isCurrentSiteExternal = configState.appDataInitialization.firstSiteData.isExternal;

    const isUserConfigImporter = !!userProfileLarge?.ListMember?.find(
        (el) => (el.Id === 'config_import' || el.Id === 'early_accessor') && el.MemberType === 'admin'
    );

    const canUserAccessNewDesign = !!userProfileLarge?.ListMember?.find(
        (el) => (el.Id === 'new_design_user') && el.MemberType === 'admin'
    );

    // Access info helper function
    const accessInfo = useMemo(
        () => {
            return getLinkAccessInfo({
                site,
                isAdmin,
                isPureDesignUser,
                selectedSiteId,
                vrsAbilities,
                useForAuthorizationCheck: false,
                isUserConfigImporter,
                canUserAccessNewDesign,
                isCurrentSiteExternal,
            });
        },
        [
            site,
            isAdmin,
            isPureDesignUser,
            selectedSiteId,
            vrsAbilities,
            isUserConfigImporter,
            isCurrentSiteExternal,
            initialised,
        ]
    );

    const isSmallNav = useMemo(() => {
        return !isSidebarOpened && smallOrHigher;
    }, [isSidebarOpened, smallOrHigher]);

    const [showAboutDialog, setShowAboutDialog] = useState(false);
    const [showEditProfileDialog, setShowEditProfileDialog] = useState(false);

    const onCloseAboutDialog = useCallback(() => {
        setShowAboutDialog(false);
    }, []);

    const onCloseEditProfileDialog = useCallback(() => {
        setShowEditProfileDialog(false);
    }, []);

    // Also add the hasAccessVrs function in the main component
    const hasAccessVrs = useCallback(
        (requestedClaim) =>
            canAccessExternalVrsPage({
                requestedClaim,
                vrsAbilities,
                isAdmin,
                useForAuthorizationCheck: false,
                isUserConfigImporter,
                canUserAccessNewDesign,
                isCurrentSiteExternal: configState.appDataInitialization.firstSiteData.isExternal,
            }),
        [isAdmin, vrsAbilities, isUserConfigImporter, configState.appDataInitialization.firstSiteData.isExternal]
    );

    // Base navigation structure
    const baseNavigation = useMemo(() => {
        return getBaseNavigation(_T, selectedSiteId, isAdmin, isUserConfigImporter, canUserAccessNewDesign);
    }, [_T, selectedSiteId, isAdmin, isUserConfigImporter, canUserAccessNewDesign]);

    const NAVIGATION = useMemo(() => {
        // Helper function to apply access control to navigation items
        const applyAccess = (item, path) => {
            const linkPath = path || `/${item.segment}`;

            // Get access information for the current item
            const access = accessInfo(linkPath);

            // Check for additional permission requirements from Sidebar.tsx logic
            let isHidden = access.hidden;

            // Check if item requires admin
            if (item.hidden || item.requireAdmin && !isAdmin || (item.requireSite && (selectedSiteId === 'VrsInternal' || selectedSiteId === '0' || selectedSiteId === ''))) {
                isHidden = true;
            }

            // Check for specific permission requirements
            if (item.can && !UserHelper.Can(item.can.do, item.can.do)) {
                isHidden = true;
            }

            // Check for module requirements
            const siteModuleMissing = item.siteModule && Utils.hasModuleMissing(site, item.siteModule);
            const extraModuleMissing = item.extraModule &&
                Utils.hasExtraModuleMissing(vrsAbilities.vrsInternalAccessAbilities, item.extraModule);

            const moduleMissing = !!(
                (item.siteModule && !item.extraModule && siteModuleMissing) ||
                (!item.siteModule && item.extraModule && extraModuleMissing) ||
                (item.siteModule && item.extraModule && siteModuleMissing && extraModuleMissing)
            );

            if (item.hideIfModuleNotEnabled && moduleMissing) {
                isHidden = true;
            }

            const isDisabled = !item.showAlways && (access.disabled || moduleMissing);

            // Apply access properties
            return {
                ...item,
                disabled: isDisabled,
                icon: item.icon ? cloneElement(item.icon, { "disabled": isDisabled ? true : undefined, 'data-nav-icon': (smallOnly && isSidebarOpened || !isSidebarOpened) ? 'small-nav' : 'normal-nav' }) : null,
                action: !item.showAlways && (access.disabled || moduleMissing) ? <div className="disabled-nav-item" /> : item.action,
                hidden: !item.showAlways && (isHidden),
                // Apply to children recursively if they exist
                ...(item.children ? {
                    children: item.children
                        .map(child => applyAccess(child, `${linkPath}/${child.segment}`))
                        .filter(child => !child.disabled && !child.hidden) // Filter out hidden and disabled children
                } : {}),
            };
        };

        // Apply access control to each navigation item
        const accessControlledNavigation = initialised ? baseNavigation
            .map(item => applyAccess(item, item.segment ? `/${item.segment}` : ''))
            .filter(item => (!item.hidden || item.kind === 'header')) : [];

        // Add full paths to the navigation items
        return addFullPathsToNavigation(accessControlledNavigation);

    }, [baseNavigation, initialised, isAuthenticated, isSmallNav, smallOnly, isSidebarOpened, selectedSiteId, _T, accessInfo, isAdmin, isUserConfigImporter, isCurrentSiteExternal]);

    const handleNavigate = (targetPath: string) => {
        console.log(`Navigating to: ${targetPath}`);
        // Use the custom navigation from context instead of direct navigate

        // Extract the path from the navigation target

        // Extract the first segment from the path
        const segments = targetPath.split('/').filter(Boolean);
        const firstSegment = segments[0];

        // Check if the segment is 'site-selection'
        if (firstSegment === 'site-selection') {
            // Cancel default navigation and call your custom method
            console.log('Intercepting site-selection navigation');
            handleSiteSelectionNavigation();
            return; // Don't proceed with navigation
        }

        customNavigate(targetPath);

        // Close mobile sidebar if open
        if (mobileOpen) {
            // You might want to add a closeMobile function to context
            // For now, this will work for desktop
        }
    };

    // Filter out headers and dividers for the mini sidebar
    const navigationItems = NAVIGATION.filter(item =>
        item.kind !== 'header' &&
        item.kind !== 'divider' &&
        !item.hidden &&
        item.segment !== 'configuration' // Exclude Configuration from main navigation
    );

    const configurationNavigationItems = NAVIGATION.filter(item =>
        item.kind !== 'header' &&
        item.kind !== 'divider' &&
        !item.hidden &&
        item.segment === 'configuration' // Include Configuration only 
    );

    const configurationItem = configurationNavigationItems.length > 0 ? configurationNavigationItems[0] : null;


    if (!isDesktop) {
        return (
            <>
                <MobileSidebar
                    mobileOpen={mobileOpen}
                    closeMobile={closeMobile}
                    navigationItems={navigationItems}
                    configurationItem={configurationItem}
                    isPureDesignUser={isPureDesignUser}
                    userProfileLarge={userProfileLarge}
                    userProfile={userProfile}
                    hasAccessVrs={hasAccessVrs}
                    setShowEditProfileDialog={setShowEditProfileDialog}
                    setShowAboutDialog={setShowAboutDialog}
                    onNavigate={handleNavigate}
                />

                <Dialogs
                    showEditProfileDialog={showEditProfileDialog}
                    onCloseEditProfileDialog={onCloseEditProfileDialog}
                    showAboutDialog={showAboutDialog}
                    onCloseAboutDialog={onCloseAboutDialog}
                    showSiteSelectionDialog={showSiteSelectionDialog}
                    onCloseSiteSelectionDialog={onCloseSiteSelectionDialog}
                />
            </>
        );
    }

    // Desktop version (permanent mini sidebar)
    return (
        <>
            <Drawer
                variant="permanent"
                PaperProps={{
                    component: "div",
                    sx: {
                        width: MINI_WIDTH,
                        overflowX: 'hidden',
                        transition: theme.transitions.create('width'),
                        backgroundColor: myVJBlack900,
                        color: myVJWhite,
                        height: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'fixed', // Make it fixed to ensure it covers the full height
                        top: 0, // Start from the very top
                        left: 0, // Start from the very left
                        zIndex: theme.zIndex.drawer, // Ensure proper layering

                        minWidth: MINI_WIDTH,
                    }
                }}
            >
                <NavigationContainer>
                    <LogoSection onClick={() => handleNavigate('/home')}>
                        <LogoImage src={logo} alt="Spinnaker Logo" />
                    </LogoSection>

                    <List disablePadding sx={{ paddingTop: 1 }}>
                        {navigationItems.map((item) => (
                            <MiniSidebarItem
                                key={item.segment || item.title}
                                item={item}
                                onNavigate={handleNavigate}
                            />
                        ))}
                    </List>
                </NavigationContainer>
                <FooterContainer>
                    <DesktopSiteSelection
                        showSiteSelectionDialog={showSiteSelectionDialog}
                        setShowSiteSelectionDialog={setShowSiteSelectionDialog}
                        isPureDesignUser={isPureDesignUser}
                    />
                    <DesktopSettingsProfile
                        configurationItem={configurationItem}
                        handleNavigate={handleNavigate}
                        isPureDesignUser={isPureDesignUser}
                    />

                    <DesktopUserProfile />
                </FooterContainer>
            </Drawer>
            <Dialogs
                showEditProfileDialog={showEditProfileDialog}
                onCloseEditProfileDialog={onCloseEditProfileDialog}
                showAboutDialog={showAboutDialog}
                onCloseAboutDialog={onCloseAboutDialog}
                showSiteSelectionDialog={showSiteSelectionDialog}
                onCloseSiteSelectionDialog={onCloseSiteSelectionDialog}
            />
        </>
    );
}
